import {
  Extension,
  Node,
  wrappingInputRule,
  type Extensions,
} from "@tiptap/core";
import { Bold } from "@tiptap/extension-bold";
import { BulletList } from "@tiptap/extension-bullet-list";
import { Document } from "@tiptap/extension-document";
import { HardBreak } from "@tiptap/extension-hard-break";
import { Heading } from "@tiptap/extension-heading";
import { HorizontalRule } from "@tiptap/extension-horizontal-rule";
import { Italic } from "@tiptap/extension-italic";
import { ListItem } from "@tiptap/extension-list-item";
import { OrderedList } from "@tiptap/extension-ordered-list";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import { Underline } from "@tiptap/extension-underline";
import { Placeholder } from "@tiptap/extensions";
import { FieldChipNode } from "./fieldChipNode";
import { FieldSuggestion, type FieldSuggestionState } from "./fieldSuggestion";
import { SessionHistory } from "./sessionHistory";
import { TextEditKeymap } from "./textEditKeymap";
import { HEADING_LEVELS, INDENT_NODE_NAME } from "./textEditModel";
import { EMPTY_TEXT_HINT } from "./textPlaceholder";
import { TextStyleMark } from "./textStyleMark";

/**
 * "- " starting a bullet list, but only at the very start of the line. TipTap's
 * own rule tolerates leading whitespace and silently swallows it, so a marker
 * typed after a tab stop would eat the spacing the author put there. Matching
 * how "1. " already behaves, whitespace before the marker keeps both.
 */
const UnindentedBulletList = BulletList.extend({
  addInputRules() {
    return [wrappingInputRule({ find: /^([-+*])\s$/, type: this.type })];
  },
});

/** Keeps markdown's tight/loose list spacing in the editing surface. */
const ListSpacing = Extension.create({
  name: "listSpacing",
  addGlobalAttributes() {
    return [
      {
        types: ["bulletList", "orderedList"],
        attributes: {
          tight: {
            default: true,
            renderHTML: (attributes) => ({ "data-tight": attributes.tight }),
          },
        },
      },
    ];
  },
});

/**
 * One indent level: whatever Tab pushes right at the start of a line. It is a
 * blockquote because that is the only container markdown can nest to any depth,
 * and it renders as a plain indent - the quote bar and italics are styled off
 * wherever quote-document markdown renders. Declared here rather than pulled
 * from `@tiptap/extension-blockquote`, which would add a dependency for a node
 * that needs no behaviour of its own.
 */
const Indent = Node.create({
  name: INDENT_NODE_NAME,
  group: "block",
  content: "block+",
  defining: true,
  parseHTML() {
    return [{ tag: "blockquote" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["blockquote", HTMLAttributes, 0];
  },
});

/** Wiring for one inline text-edit session's extension set. */
export interface TextEditExtensionOptions {
  /**
   * Whether the session root can hold block structure: list, heading and
   * divider nodes join the schema and Enter splits into a new paragraph.
   */
  blocksEnabled: boolean;
  /** Commits the session (Cmd/Ctrl+Enter). */
  onCommit: () => void;
  /** Cancels the session (Escape). */
  onCancel: () => void;
  /** Reports the "]" field-menu trigger state. */
  onFieldMenuState: (state: FieldSuggestionState | null) => void;
}

/**
 * Builds the explicit, minimal extension set for one inline text-edit session,
 * matching what markdown can express: doc/paragraph/text/hardBreak, bold,
 * italic, underline and per-run size/colour marks, field chips, session
 * undo/redo, the session keymap, the
 * empty-block placeholder, and the "]" field-menu trigger. Lists, headings and
 * dividers join the schema only when the session root supports block
 * structure, which also brings their markdown input rules, each of which fires
 * only at the very start of a line: "- " or "1. " starts a list, "# " to
 * "#### " a heading, "---" a divider. They are the whole reason the palette offers no
 * heading, list or divider block - a text block grows them as they are typed.
 * An indented line is left alone, markdown having no way to store a list that
 * sits anywhere but the margin.
 *
 * Underline has no markdown syntax, so the commit serializes it as the `<u>`
 * inline HTML the renderer reads back; a run's own size or colour serializes
 * the same way, as a `<span size="..." color="...">`.
 *
 * Undo/redo is scoped to the session (SessionHistory); document-level history
 * in history.ts takes over once the session commits.
 * @param options - The session wiring callbacks and capabilities
 * @returns The TipTap extensions for the session editor
 */
export function buildTextEditExtensions(
  options: TextEditExtensionOptions,
): Extensions {
  const base: Extensions = [
    Document,
    Paragraph,
    Text,
    HardBreak,
    Bold,
    Italic,
    Underline,
    TextStyleMark,
    FieldChipNode,
    SessionHistory,
    TextEditKeymap.configure({
      blocksEnabled: options.blocksEnabled,
      onCommit: options.onCommit,
      onCancel: options.onCancel,
    }),
    FieldSuggestion.configure({ onState: options.onFieldMenuState }),
    Placeholder.configure({ placeholder: EMPTY_TEXT_HINT }),
  ];
  if (!options.blocksEnabled) {
    return base;
  }
  return [
    ...base,
    UnindentedBulletList,
    OrderedList,
    ListItem,
    ListSpacing,
    Indent,
    Heading.configure({ levels: [...HEADING_LEVELS] }),
    HorizontalRule,
  ];
}
